import * as CONSTANTS from "../utils/constants/constants";
import axios from "axios";
import ClinicService from "./ClinicService";

/**
 * PatientCohortService - Handles patient assignment and management within cohorts
 * This service provides high-level operations for managing patient-cohort relationships
 */
class PatientCohortService {
    
    // ================================
    // Patient Assignment Operations
    // ================================

    /**
     * Assign a single patient to a cohort
     */
    static async assignPatientToCohort(
        cohortId: number, 
        patientId: string, 
        notes: string = "", 
        access_token: string
    ) {
        const data = {
            cohort_id: cohortId,
            patient_id: patientId,
            notes: notes,
            is_active: true
        };

        try {
            const response = await ClinicService.associatePatient(data, access_token);
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error assigning patient to cohort:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to assign patient" 
            };
        }
    }

    /**
     * Assign multiple patients to a cohort (bulk operation)
     */
    static async bulkAssignPatientsToCohort(
        cohortId: number, 
        patientIds: string[], 
        notes: string = "", 
        access_token: string
    ) {
        const data = {
            cohort_id: cohortId,
            patient_ids: patientIds,
            notes: notes
        };

        try {
            const response = await ClinicService.bulkAssociatePatients(data, access_token);
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error bulk assigning patients to cohort:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to assign patients" 
            };
        }
    }

    /**
     * Remove a patient from a cohort
     */
    static async removePatientFromCohort(
        cohortId: number, 
        patientId: string, 
        access_token: string
    ) {
        const data = {
            cohort_id: cohortId,
            patient_id: patientId
        };

        try {
            const response = await ClinicService.removePatientFromCohort(data, access_token);
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error removing patient from cohort:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to remove patient" 
            };
        }
    }

    /**
     * Remove multiple patients from a cohort (bulk operation)
     */
    static async bulkRemovePatientsFromCohort(
        cohortId: number, 
        patientIds: string[], 
        access_token: string
    ) {
        const data = {
            cohort_id: cohortId,
            patient_ids: patientIds
        };

        try {
            const response = await ClinicService.bulkRemovePatients(data, access_token);
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error bulk removing patients from cohort:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to remove patients" 
            };
        }
    }

    /**
     * Transfer a patient from one cohort to another
     */
    static async transferPatientBetweenCohorts(
        patientId: string,
        fromCohortId: number,
        toCohortId: number,
        notes: string = "",
        access_token: string
    ) {
        try {
            // First remove from source cohort
            const removeResult = await this.removePatientFromCohort(
                fromCohortId, 
                patientId, 
                access_token
            );

            if (!removeResult.success) {
                return removeResult;
            }

            // Then add to destination cohort
            const assignResult = await this.assignPatientToCohort(
                toCohortId, 
                patientId, 
                `Transferred from cohort ${fromCohortId}. ${notes}`.trim(), 
                access_token
            );

            return assignResult;

        } catch (error: any) {
            console.error("Error transferring patient between cohorts:", error);
            return { 
                success: false, 
                error: "Failed to transfer patient between cohorts" 
            };
        }
    }

    // ================================
    // Patient Query Operations
    // ================================

    /**
     * Get all patients assigned to a specific cohort
     */
    static async getCohortPatients(cohortId: number, access_token: string) {
        try {
            const url = ClinicService.getPatientAssociations(cohortId.toString());
            const response = await axios.get(url, {
                headers: { 
                    authorization: "Token " + access_token 
                }
            });
            
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error fetching cohort patients:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to fetch patients" 
            };
        }
    }

    /**
     * Get all cohorts a specific patient is assigned to
     */
    static async getPatientCohorts(patientId: string, access_token: string) {
        try {
            const url = ClinicService.getPatientAssociations(undefined, patientId);
            const response = await axios.get(url, {
                headers: { 
                    authorization: "Token " + access_token 
                }
            });
            
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error fetching patient cohorts:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to fetch cohorts" 
            };
        }
    }

    /**
     * Search for unassigned patients (not in any cohort)
     */
    static async getUnassignedPatients(organizationId: string, access_token: string) {
        try {
            // This would need to be implemented based on your patient service
            // For now, we'll return a placeholder
            const url = CONSTANTS.HOST + `api/v1/patients/unassigned/?organization=${organizationId}`;
            const response = await axios.get(url, {
                headers: { 
                    authorization: "Token " + access_token 
                }
            });
            
            return { success: true, data: response.data };
        } catch (error: any) {
            console.error("Error fetching unassigned patients:", error);
            return { 
                success: false, 
                error: error.response?.data?.error || "Failed to fetch unassigned patients" 
            };
        }
    }

    // ================================
    // Legacy Compatibility Methods
    // ================================

    /**
     * Legacy method for device-based operations - maps to patient operations
     * @deprecated Use patient-based methods instead
     */
    static async manageDeviceAssignments(
        groupId: string, 
        operation: 'attach' | 'detach', 
        deviceIds: string[], 
        access_token: string
    ) {
        console.warn("manageDeviceAssignments is deprecated. Use patient assignment methods instead.");
        
        // Map device operations to patient operations for backward compatibility
        const cohortId = parseInt(groupId);
        
        if (operation === 'attach') {
            return await this.bulkAssignPatientsToCohort(
                cohortId, 
                deviceIds, 
                "Migrated from device assignment", 
                access_token
            );
        } else {
            return await this.bulkRemovePatientsFromCohort(
                cohortId, 
                deviceIds, 
                access_token
            );
        }
    }

    /**
     * Legacy method - maps device groups to cohorts
     * @deprecated Use getCohortPatients instead
     */
    static getGroupPatients(cohortId: string) {
        console.warn("getGroupPatients is deprecated. Use getCohortPatients instead.");
        return ClinicService.getPatientAssociations(cohortId);
    }

    // ================================
    // Utility Methods
    // ================================

    /**
     * Validate if a patient can be assigned to a cohort
     */
    static async validatePatientAssignment(
        patientId: string, 
        cohortId: number, 
        access_token: string
    ) {
        try {
            // Check if patient is already assigned to this cohort
            const existingAssociations = await this.getPatientCohorts(patientId, access_token);
            
            if (existingAssociations.success && existingAssociations.data) {
                const isAlreadyAssigned = existingAssociations.data.results?.some(
                    (assoc: any) => assoc.cohort === cohortId && assoc.is_active
                );
                
                if (isAlreadyAssigned) {
                    return { 
                        valid: false, 
                        reason: "Patient is already assigned to this cohort" 
                    };
                }
            }
            
            return { valid: true };
        } catch (error) {
            console.error("Error validating patient assignment:", error);
            return { 
                valid: false, 
                reason: "Unable to validate assignment" 
            };
        }
    }

    /**
     * Get assignment statistics for a cohort
     */
    static async getCohortStatistics(cohortId: number, access_token: string) {
        try {
            const patientsResult = await this.getCohortPatients(cohortId, access_token);
            
            if (!patientsResult.success) {
                return patientsResult;
            }
            
            const associations = patientsResult.data.results || [];
            const activeAssociations = associations.filter((assoc: any) => assoc.is_active);
            
            return {
                success: true,
                data: {
                    total_associations: associations.length,
                    active_patients: activeAssociations.length,
                    inactive_associations: associations.length - activeAssociations.length,
                    recent_assignments: associations
                        .filter((assoc: any) => {
                            const assignedDate = new Date(assoc.assigned_at);
                            const weekAgo = new Date();
                            weekAgo.setDate(weekAgo.getDate() - 7);
                            return assignedDate > weekAgo;
                        })
                        .length
                }
            };
        } catch (error: any) {
            console.error("Error fetching cohort statistics:", error);
            return { 
                success: false, 
                error: "Failed to fetch cohort statistics" 
            };
        }
    }
}

export default PatientCohortService;